<template>
	<div class="flex flex-column height-100">
		<ProfileAndAsyncHeader v-on="$listeners" />
		<ActionCreditsRequiredBadge v-if="actionInfo" :action="actionInfo" utilClasses="mx-5 my-1" />
		<div class="parmas-container height-100 px-5">
			<h2 class="mb-3 ml-1 mt-0">{{ settingsTitle }}</h2>
			<div v-if="isMapperMode">
				<ActionParamsDispatcherDataProvider :node="editNode" />
			</div>
			<ParamsForm
				v-else
				@test-and-continue="testAndContinue"
				@update-block-param="updateBlockParam"
				@update-params-fallback="updateParamsFallback"
				@update-block-param-label="updateBlockParamLabels"
				:key="`${editNode.id}__${editNode.service}__${editNode.action}`"
			/>
			<AdvancedSettings v-if="showAdvancedSettings" class="mt-6" />
		</div>
	</div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { mapActions, mapGetters } from 'vuex';

import ActionParamsDispatcherDataProvider from './ActionParamsDispatcherDataProvider';
import ParamsForm from './ParamsForm';
import ProfileAndAsyncHeader from '../StepDetails/ProfileAndAsyncHeader';
import ActionCreditsRequiredBadge from '@/components/BlockEditor/IntegrationEditor/ActionCreditsRequiredBadge.vue';
import AdvancedSettings from '@/components/BlockEditor/IntegrationEditor/ProfileSelection/AdvancedSettings';
import { getActionParams } from '@/store/modules/actions/getters';
import { editNode } from '@/store/modules/selectedNode/getters';
import { actionContextNode, element } from '@/store/modules/selectedWorkflow/getters';
import { PROFILE_FROM_CONFIGURATION_CREDENTIALS_TYPES } from '@/utils/credentials-service.const';

export default {
	name: 'BlockEditorParams',
	props: { showAdvancedSettings: { type: Boolean } },
	components: {
		ParamsForm,
		AdvancedSettings,
		ProfileAndAsyncHeader,
		ActionParamsDispatcherDataProvider,
		ActionCreditsRequiredBadge,
	},
	computed: {
		...mapGetters(['actions/getAction']),
		...mapGetters('selectedNode', ['editNode']),
		...mapGetters([getActionParams.fullPath, element.fullPath, editNode.fullPath, actionContextNode.fullPath]),
		relevantEditNode() {
			return this.isWebhook && this[element.fullPath] ? this[actionContextNode.fullPath] : this[editNode.fullPath];
		},
		actionParams() {
			return this[getActionParams.fullPath](this.relevantEditNode.action);
		},
		actionInfo() {
			return this['actions/getAction'](this.editNode.action);
		},
		settingsTitle() {
			return this.actionInfo?.settingsTitle || '';
		},
		isMapperMode() {
			return (
				PROFILE_FROM_CONFIGURATION_CREDENTIALS_TYPES.includes(this.editNode.credentials_configuration?.credentials_type) &&
				this.actionInfo?.has_user_schema
			);
		},
	},
	methods: {
		...mapActions('selectedNode', ['setParamInEditNode']),
		testAndContinue() {
			this.$emit('test-and-continue');
		},
		showSelectAppScreen() {
			this.$emit('showSelectAppScreen');
		},
		async updateBlockParam(payload) {
			const { key, value, reloadDynamic, preventUpdateEditNode, syncEditNodeAfterUpdate } = payload;
			const paramsMap = cloneDeep(this.editNode.params_map) || {};
			paramsMap[key] = value;
			if (!syncEditNodeAfterUpdate) {
				this.handleBlockParamDependecies(key);
			}
			this.setParamInEditNode({
				key: 'params_map',
				value: paramsMap,
				options: { reloadDynamic, preventUpdateEditNode, syncEditNodeAfterUpdate },
			});
		},
		updateBlockParamLabels(payload) {
			const { key, value, preventUpdateEditNode } = payload;
			const paramsMapLabels = cloneDeep(this.editNode.params_map_labels) || {};
			paramsMapLabels[key] = value;
			this.setParamInEditNode({ key: 'params_map_labels', value: paramsMapLabels, options: { preventUpdateEditNode } });
		},
		removeParamKey(key) {
			const paramsMap = cloneDeep(this.editNode.params_map) || {};
			delete paramsMap[key];
			this.setParamInEditNode({ key: 'params_map', value: paramsMap });
		},
		removeParamLabel(key) {
			const paramsMapLabels = cloneDeep(this.editNode.params_map_labels) || {};
			delete paramsMapLabels[key];
			this.setParamInEditNode({ key: 'params_map_labels', value: paramsMapLabels });
		},
		updateParamsFallback(payload) {
			const { key, value } = payload;
			const fallbacks = cloneDeep(this.editNode.fallbacks) || {};
			fallbacks[key] = value;
			this.setParamInEditNode({ key: 'fallbacks', value: fallbacks });
		},
		handleBlockParamDependecies(keyChanged) {
			this.actionParams.forEach((param) => {
				const dependsOn = param.depends_on || [];
				if (dependsOn.includes(keyChanged)) {
					this.removeParamKey(param.key);
					this.removeParamLabel(param.key);
				}
			});
		},
	},
};
</script>

<style scoped lang="scss">
.parmas-container {
	max-width: 480px;
	margin-bottom: 80px;
	overflow-y: auto;
}
</style>
